import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { formatTimeAgo } from "@/components/agent/display/utils/formatters";
import { GenieAvatar } from "@/components/GenieAvatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Agent } from "@/types/agent/agent";
import { GenieConsoleState } from "@/types/dashboard/genie-console";
import { GenieSwitcher } from "./GenieSwitcher";
import { GenieBrand, resolveGenieBrand } from "./genieBrand";

/**
 * The full-body Help Genie. Transparent background, so it composites on the
 * light header — unlike `/genie-mascots/role/*.png`, which are matted on black.
 * `/icons/genie-character.png` used to sit here at 150px despite being a 60px
 * icon, which is why it read as a blurry purple blob rather than a character.
 */
const MASCOT_SRC = "/genie-mascots/loading-genie.png";

interface GenieIdentityHeaderProps {
  state: GenieConsoleState;
  onSelectGenie: (genieId: string) => void;
  /** The computed do-this-next line, rendered directly under the identity. */
  children?: ReactNode;
}

/**
 * One live sentence about what this genie has actually been doing.
 * Falls back through the bands so a half-loaded console still reads sensibly.
 */
const activitySentence = (state: GenieConsoleState, genie: Agent | null): string => {
  if (!genie) return "Pick a Genie to see how it's doing.";

  const parts: string[] = [];
  const documents = state.knowledge.data.documentCount;
  const conversations = state.voice.data.conversationCount;
  const lastAt = state.voice.data.lastConversationAt;

  parts.push(
    documents === 0
      ? "No knowledge yet"
      : `Answering from ${documents} ${documents === 1 ? "document" : "documents"}`,
  );

  if (conversations > 0) {
    parts.push(
      `${conversations} ${conversations === 1 ? "conversation" : "conversations"} in the last 30 days`,
    );
    if (lastAt) parts.push(`last heard ${formatTimeAgo(lastAt)}`);
  } else {
    parts.push("no conversations yet");
  }

  return `${parts.join(" · ")}.`;
};

/**
 * The Genie identity header (HG-1229).
 *
 * Replaces the generic dashboard `PageHeader` with the genie itself: a large
 * animated avatar in the genie's own brand colour, the genie's name as the
 * page title, a one-line live activity sentence, and — when the account has
 * more than one — a horizontal switcher. Motion is ambient only: a slow
 * breathing scale, no spin and no pulse, and it stops entirely under
 * `prefers-reduced-motion`.
 */
/**
 * The mascot standing in the identity slot, lit by the genie's own brand colour.
 * The artwork is never recoloured — the halo behind it carries the brand, so a
 * red-branded genie still gets a genie rather than a red-washed one.
 */
const GenieMascot = ({ brand }: { brand: GenieBrand }) => (
  <div className="relative flex h-[150px] w-[124px] items-end justify-center md:h-[180px] md:w-[150px]">
    <div
      aria-hidden="true"
      className="absolute inset-x-0 bottom-2 top-6 rounded-full blur-2xl"
      style={{
        background: `radial-gradient(58% 52% at 50% 62%, ${brand.primary}40 0%, rgba(255,255,255,0) 72%)`,
      }}
    />
    <img
      src={MASCOT_SRC}
      alt="Help Genie"
      className="relative h-full w-auto object-contain"
      style={{ filter: `drop-shadow(0 12px 22px ${brand.primary}33)` }}
    />
  </div>
);

export const GenieIdentityHeader = ({
  state,
  onSelectGenie,
  children,
}: GenieIdentityHeaderProps) => {
  const genie = state.genie.data.selectedGenie;
  const brand = resolveGenieBrand(genie);
  const isLoading = state.genie.isLoading;

  return (
    <header className="mb-8">
      {/* Brand wash behind the identity — the genie's colour, not a global one. */}
      <div
        className="rounded-[24px] p-[1.5px] shadow-sm transition-all duration-500"
        style={{ background: brand.fill }}
      >
        <div className="rounded-[22.5px] bg-white">
          <div
            className="rounded-[22.5px] px-5 py-6 transition-all duration-500 md:px-8 md:py-8"
            style={{
              background: `radial-gradient(120% 140% at 12% 0%, ${brand.primary}14 0%, rgba(255,255,255,0) 60%)`,
            }}
          >
            <div className="flex flex-col gap-5 md:flex-row md:items-center md:gap-8">
              {/*
                The genie is a character, never a letterform. The customer's own
                logo wins when they have uploaded one — that is their brand — and
                every other case gets the mascot, including a genie that has only
                picked brand colours. The old default drew a "G" in a circle.
              */}
              <div className="genie-dashboard-breathe flex-shrink-0 self-start md:self-center">
                {isLoading ? (
                  <Skeleton className="h-[150px] w-[124px] rounded-3xl md:h-[180px] md:w-[150px]" />
                ) : brand.logoUrl ? (
                  <GenieAvatar
                    size={150}
                    color={brand.primary}
                    secondaryColor={brand.secondary}
                    gradientEnabled={brand.gradientEnabled}
                    gradientAngle={brand.gradientAngle}
                    customAvatarUrl={brand.logoUrl}
                    hasCustomBranding={brand.hasCustomBranding}
                  />
                ) : (
                  <GenieMascot brand={brand} />
                )}
              </div>

              <div className="min-w-0 flex-1">
                {isLoading ? (
                  <>
                    <Skeleton className="h-9 w-64" />
                    <Skeleton className="mt-3 h-5 w-96 max-w-full" />
                  </>
                ) : (
                  <>
                    <h1
                      className="truncate text-3xl font-bold leading-tight text-[#222B45] transition-colors duration-500 md:text-4xl"
                      style={genie ? { color: brand.primary } : undefined}
                    >
                      {genie?.name || "Your Dashboard"}
                    </h1>
                    <p className="mt-2 text-sm text-[#64748B] md:text-base">
                      {activitySentence(state, genie)}
                    </p>
                    {genie && (
                      <Link
                        to={`/genie-hub/my-genies/${genie.id}`}
                        className="mt-3 inline-flex text-sm font-semibold text-genie-primary hover:underline"
                      >
                        Manage this Genie
                      </Link>
                    )}
                  </>
                )}

                {children ? <div className="mt-5">{children}</div> : null}
              </div>
            </div>

            {state.genie.data.genies.length > 1 && (
              <div className="mt-6 border-t border-[#F1F3F7] pt-5">
                <GenieSwitcher
                  genies={state.genie.data.genies}
                  selectedGenieId={state.agentId}
                  onSelect={onSelectGenie}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/*
        Ambient motion per guidelines/design.md: a slow breathing SCALE only.
        No spin, no pulse, no opacity flicker. Scoped here rather than added to
        the shared index.css so this branch stays self-contained.
      */}
      <style>{`
        .genie-dashboard-breathe {
          animation: genieDashboardBreathe 7s ease-in-out infinite;
          will-change: transform;
        }
        @keyframes genieDashboardBreathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.022); }
        }
        @media (prefers-reduced-motion: reduce) {
          .genie-dashboard-breathe { animation: none; }
        }
      `}</style>
    </header>
  );
};

export default GenieIdentityHeader;
